import React, { useEffect, useState, useCallback, useMemo, useRef, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Paper, useTheme, Chip, Alert } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  PageContainer,
  BreadcrumbContainer,
  BreadcrumbLinkComponent,
  PageHeader,
} from 'app/components/StyledComponents';
import { Breadcrumbs } from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import DataGridMAIQ, { DataGridMAIQRef } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import ModalMAIQ from 'app/components/ModalMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import LoadingIndicator from 'app/components/LoadingIndicator';
import { useRunLogs } from 'app/context/RunLogsContext';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import RunLogDetailsModal, { SimpleRunLogDetailsModal } from './components/RunLogDetailsModal';
import UploadModal from './components/UploadModal';
import { RunLog } from './types';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { useQueryClient } from '@tanstack/react-query';
// import SocketTestComponent from 'app/components/SocketTestComponent';
import SocketMonitoringPanel from 'app/components/SocketMonitoringPanel';
// import RunLogSimulator from 'app/components/RunLogSimulator';
import ErrorBoundary from 'app/components/ErrorBoundary';
// import SocketToastManager, { ToastNotification } from 'app/components/SocketToastManager';


function RunLogsPage() {
  const history = useHistory();
  const runLogsContext = useRunLogs();
  const selectedDealer = useSelector(selectDealerSelected);
  const [selectedRunLog, setSelectedRunLog] = useState<RunLog | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [dataGridKey, setDataGridKey] = useState<string>('0');
  const [activeRunLogIds, setActiveRunLogIds] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [datagridData, setDatagridData] = useState<any[]>([]);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [isSocketPanelOpen, setIsSocketPanelOpen] = useState(false);
  // const [toastNotifications, setToastNotifications] = useState<ToastNotification[]>([]);
  const theme = useTheme();
  const queryClient = useQueryClient();
  
  // Ref to access the DataGridMAIQ component
  const dataGridRef = useRef<DataGridMAIQRef>(null);

  // Destructure context values with fallbacks
  const {
    loading = false,
    fetchRunByRunId = async () => {},
    clearSelectedRunLog = () => {},
    selectedRunLog: contextSelectedRunLog = null,
    socketConnected = false,
    realTimeRunLogs = [],
    subscribeToRunLog = () => {},
    unsubscribeFromRunLog = () => {},
    error: contextError = null
  } = runLogsContext || {};

  // Combine errors
  const error = localError || contextError;

  // Memoized dealer ID to prevent unnecessary re-renders
  const dealerId = useMemo(() => selectedDealer?._id, [selectedDealer?._id]);





  // Handle real-time run log status updates with better error handling
  const handleRunLogStatusUpdate = useCallback(
    (runLogEvent: any) => {
      try {

        // Validate runLogEvent
        if (!runLogEvent || typeof runLogEvent !== 'object') {
          return;
        }

        // Handle nested message structure from backend (as shown in the WebSocket messages)
        let eventData = runLogEvent;
        if (runLogEvent.message && typeof runLogEvent.message === 'object') {
          // Backend sends data in nested message object
          eventData = runLogEvent.message;
        }

        const runLogId = eventData.run_log_id || eventData.runId || eventData.id;
        if (!runLogId) {
          return;
        }

        const status = eventData.status || 'unknown';
        const message = eventData.message || 'No message available';


        // Prepare updates object for manual update
        const updates = {
          status: status,
        };

        // Call manual update function from DataGridMAIQ ref
        if (dataGridRef.current) {
          dataGridRef.current.manualUpdateData(runLogId, updates);
        }

        // Get current cache data to debug
        const currentCacheData = queryClient.getQueryData([
          'GET',
          `${DataGridTypes.API_VERSION_2}/run-v2`,
          { dealer: dealerId },
        ]);

        // Update the correct query cache for RUN_LOGS endpoint
        const updatedData = queryClient.setQueryData(
          [
            'GET',
            `${DataGridTypes.API_VERSION_2}/run-v2`,
            { dealer: dealerId },
          ],
          (oldData: any) => {
            
            if (!oldData || !oldData.body || !oldData.body.data) {
              return oldData;
            }


            const updatedData = { ...oldData };
            const runLogIndex = updatedData.body.data.findIndex(
              (runLog: any) => runLog._id === runLogId,
            );


            if (runLogIndex >= 0) {
              
              
              // Update the status field and add real-time update timestamp
              updatedData.body.data[runLogIndex] = {
                ...updatedData.body.data[runLogIndex],
                status: status, // Update the 'status' field that the datagrid displays
                run_status: status, // Also update run_status for consistency
                last_update: new Date().toISOString(),
                progress: eventData.progress || undefined,
                message: message,
                // Add additional fields that might be useful
                end_timestamp: eventData.end_timestamp || undefined,
                duration_ms: eventData.duration_ms || undefined,
                total_records: eventData.total_records || undefined,
                processed_records: eventData.processed_records || undefined,
              };
              
            } 

            return updatedData;
          },
        );


        // Also try updating with the alternative query key format
        queryClient.setQueryData(
          [
            'GET',
            `${DataGridTypes.API_VERSION_2}/run-v2`,
            { dealer_id: dealerId },
          ],
          (oldData: any) => {
            if (!oldData || !oldData.body || !oldData.body.data) {
              return oldData;
            }

            const updatedData = { ...oldData };
            const runLogIndex = updatedData.body.data.findIndex(
              (runLog: any) => runLog._id === runLogId,
            );

            if (runLogIndex >= 0) {
              updatedData.body.data[runLogIndex] = {
                ...updatedData.body.data[runLogIndex],
                status: status,
                run_status: status,
                last_update: new Date().toISOString(),
                progress: eventData.progress || undefined,
                message: message,
                end_timestamp: eventData.end_timestamp || undefined,
                duration_ms: eventData.duration_ms || undefined,
                total_records: eventData.total_records || undefined,
                processed_records: eventData.processed_records || undefined,
              };
            }

            return updatedData;
          },
        );

        // Don't force invalidate queries for socket updates - let manual updates handle it
        // queryClient.invalidateQueries({
        //   queryKey: ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`],
        //   exact: false
        // });

        // Don't force DataGrid re-render for socket updates - manual updates are sufficient
        // setForceRefreshCount(prev => prev + 1);

      } catch (error) {
        setLocalError('Failed to update run log status');
      }
          },
      [queryClient, dealerId],
    );

  // Subscribe to run log updates when dealer changes
  useEffect(() => {
    if (dealerId) {
      setDataGridKey(Date.now().toString());
    }
  }, [dealerId]);

  // Subscribe to real-time run log events
  useEffect(() => {
    if (socketConnected && dealerId) {
    }
  }, [socketConnected, dealerId]);

  // Refresh datagrid when upload modal closes - REMOVED to prevent double refresh
  // The refresh is now handled only in handleUploadSuccess and handleCloseUploadModal

  // Handle real-time run log status updates from context with better error handling
  useEffect(() => {
    try {
      if (!Array.isArray(realTimeRunLogs)) {
        return;
      }


      let hasStatusChanges = false;

      realTimeRunLogs.forEach((runLog, index) => {
        try {
          // Validate runLog object
          if (!runLog || typeof runLog !== 'object') {
            return;
          }

          const runLogId = runLog.run_log_id || runLog.runId || runLog.id;
          if (!runLogId) {
            return;
          }


          // Update the datagrid with the new status
          handleRunLogStatusUpdate(runLog);
          hasStatusChanges = true;

          // Track active run log IDs for subscription management
          if (
            runLog.status === 'running' &&
            !activeRunLogIds.includes(runLogId)
          ) {
            setActiveRunLogIds(prev => [...prev, runLogId]);
            subscribeToRunLog(runLogId);
          } else if (
            runLog.status !== 'running' &&
            activeRunLogIds.includes(runLogId)
          ) {
            setActiveRunLogIds(prev => prev.filter(id => id !== runLogId));
            unsubscribeFromRunLog(runLogId);
          }

          // Log status changes for monitoring
          
        } catch (error) {
        }
      });

      // Don't force datagrid refresh for socket updates - manual updates are sufficient
      // if (hasStatusChanges) {
      //   setDataGridKey(Date.now().toString());
      // }
    } catch (error) {
      setLocalError('Failed to process real-time updates');
    }
  }, [
    realTimeRunLogs,
    handleRunLogStatusUpdate,
    subscribeToRunLog,
    unsubscribeFromRunLog,
    activeRunLogIds,
  ]);

  // This is the ONLY place where datagrid refresh happens after upload
  // Called by UploadModal's onUploadSuccess callback after successful upload
  const handleUploadSuccess = useCallback(() => {
    try {
      // Invalidate run logs cache with correct query key
      queryClient.removeQueries({
        queryKey: ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`],
        exact: false
      });
      
      // Force re-render by updating the key
      setDataGridKey(Date.now().toString());
      
      console.log('✅ Upload success handled - cache invalidated and datagrid refreshed');
    } catch (error) {
      setLocalError('Failed to refresh data after upload');
    }
  }, [queryClient]);

  // This only closes the modal - no refresh needed here
  // Refresh is handled by handleUploadSuccess when upload actually completes
  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const handleCellClick = useCallback((cell: any) => {
    try {
      if (!cell || !cell.field || !cell.row) {
        return;
      }

      if (cell.field === '_id') {
        
        // Validate dealer ID
        if (!dealerId) {
          setLocalError('Please select a dealer first');
          return;
        }

        // Use the context to fetch detailed run log data
        fetchRunByRunId(cell.row._id, dealerId);
        setIsModalOpen(true);
      }
    } catch (error) {
      setLocalError('Failed to open run log details');
    }
  }, [fetchRunByRunId, dealerId]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    clearSelectedRunLog();
  }, [clearSelectedRunLog]);

  const handleOpenUploadModal = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  const handleOpenSocketPanel = useCallback(() => {
    setIsSocketPanelOpen(true);
  }, [isSocketPanelOpen]);

  const handleCloseSocketPanel = useCallback(() => {
    setIsSocketPanelOpen(false);
  }, []);

  // Clear local error when context error changes
  useEffect(() => {
    if (contextError) {
      setLocalError(null);
    }
  }, [contextError]);

  // If no dealer is selected, show a message
  if (!selectedDealer || !dealerId) {
    return (
      <>
        <Helmet>
          <title>Run Logs</title>
          <meta name="description" content="Run Logs - Myautoiq" />
        </Helmet>
        <PageContainer>
          <Typography
            variant="h4"
            gutterBottom
            component="div"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              fontWeight: 'normal',
            }}
          >
            Please select a dealer
          </Typography>
        </PageContainer>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Run Logs</title>
        <meta name="description" content="Run Logs - Myautoiq" />
      </Helmet>
      {loading && <LoadingIndicator />}
      <PageContainer>
        {/* Error Display */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setLocalError(null)}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <BreadcrumbContainer>
            <Breadcrumbs
              aria-label="breadcrumb"
              separator={<NavigateNextIcon fontSize="small" />}
            >
              <BreadcrumbLinkComponent underline="hover" color="inherit">
                {selectedDealer?.name || 'Dealer'}
              </BreadcrumbLinkComponent>
              <BreadcrumbLinkComponent underline="hover" color="text.primary">
                Run Logs
              </BreadcrumbLinkComponent>
            </Breadcrumbs>
          </BreadcrumbContainer>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
            boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DirectionsRunIcon
              sx={{
                color: theme.palette.secondary.main,
                fontSize: 28,
              }}
            />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: theme.palette.secondary.main,
                }}
              >
                Run Logs
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.85rem' }}
              >
                View and manage run logs for{' '}
                {selectedDealer?.name || 'selected dealer'}
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
              >
                <Chip
                  label={
                    socketConnected
                      ? 'Real-time Connected'
                      : 'Real-time Disconnected'
                  }
                  color={socketConnected ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                  onClick={handleOpenSocketPanel}
                  sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                />
                {/* Debug info */}
                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  Panel: {isSocketPanelOpen ? 'Open' : 'Closed'}
                </Typography>
                {activeRunLogIds.length > 0 && (
                  <Chip
                    label={`${activeRunLogIds.length} Active`}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                )}
                {realTimeRunLogs.length > 0 && (
                  <Chip
                    label={`${realTimeRunLogs.length} Updates`}
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpenUploadModal}
            startIcon={<UploadIcon />}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: '#0a1f3a',
                boxShadow: '0 4px 10px rgba(15, 40, 76, 0.2)',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Upload Raw Data
          </Button>
        </Paper>

        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 1.5,
            boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <DataGridMAIQ
            ref={dataGridRef}
            type={DataGridTypes.RUN_LOGS(dealerId)}
            onCellClick={handleCellClick}
            key={`${dataGridKey}-${forceRefreshCount}`} // Force re-render only for upload/refresh, not socket updates
          />
        </Box>
      </PageContainer>

      {contextSelectedRunLog && (
        <ErrorBoundary>
          <SimpleRunLogDetailsModal
            open={isModalOpen}
            onClose={handleCloseModal}
            runLog={contextSelectedRunLog}
          />
        </ErrorBoundary>
      )}

      <ErrorBoundary>
        <UploadModal
          open={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          onUploadSuccess={handleUploadSuccess}
        />
      </ErrorBoundary>

      {/* Socket Monitoring Panel Popup */}
      <ErrorBoundary>
        <ModalMAIQ
          open={isSocketPanelOpen}
          onClose={handleCloseSocketPanel}
          hideDefaultCloseButton={false}
        >
          <Box
            sx={{
              width: '90vw',
              maxWidth: '1200px',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                borderRadius: 0,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#0F2A52',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <Typography variant="h6" fontWeight="600">
                  Socket Monitoring Panel
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 400, fontSize: '0.8rem' }}>
                  Real-time socket connection and run log monitoring
                </Typography>
              </Box>

              {/* Content */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                }}
              >
                <SocketMonitoringPanel />
              </Box>
            </Paper>
          </Box>
        </ModalMAIQ>
      </ErrorBoundary>
    </>
  );
}

export default RunLogsPage;
